import type { TFunction } from 'i18next';
import type { ReservationBooking } from '../../services/reservation-system/reservation-board.service';
import {
  formatRsBookingTimeRange,
  rsBookingDisplayName,
  visibleRsBookingNotes,
} from '../../utils/reservationBoardCalendar.util';
import styles from './ReservationBoardCalendar.module.css';

type RsCalendarBookingCardProps = {
  booking: ReservationBooking;
  deletePending: boolean;
  onPrefillAfterBooking?: (booking: ReservationBooking) => void;
  onDeleteBooking: (booking: ReservationBooking) => void;
  t: TFunction;
};

/** カレンダー上の確定予約。氏名は表示のみ。操作は「続けて予約」と削除 */
export default function RsCalendarBookingCard({
  booking,
  deletePending,
  onPrefillAfterBooking,
  onDeleteBooking,
  t,
}: RsCalendarBookingCardProps) {
  const deleteLabel = booking.hasDeletePassword
    ? t('reservation.board.deletePasswordProtected', {
        defaultValue: '削除パスワードが設定されています',
      })
    : t('common.delete', { defaultValue: '削除' });
  const timeLabel = formatRsBookingTimeRange(
    booking.startTime,
    booking.endTime,
    t('reservation.board.timeRangeSeparator', { defaultValue: '〜' }),
    t('reservation.board.overnightPrefix', { defaultValue: '翌' })
  );
  const nameLabel = rsBookingDisplayName(booking);
  const notes = visibleRsBookingNotes(booking.notes);

  return (
    <div
      className={`${styles.bookingItem}${
        onPrefillAfterBooking ? ` ${styles.bookingItemHasPrefill}` : ''
      }`}
    >
      <div className={styles.bookingItemMain}>
        <span className={styles.bookingMainRow}>
          <span className={styles.bookingTime}>{timeLabel}</span>
          <span className={styles.bookingLabel}>{nameLabel}</span>
        </span>
        {notes ? <div className={styles.bookingNotes}>{notes}</div> : null}
      </div>
      {onPrefillAfterBooking ? (
        <button
          type="button"
          className={styles.bookingPrefillBtn}
          title={t('reservation.board.prefillAfterBookingHint', {
            defaultValue: 'この予約の直後の枠をフォームに入れます',
          })}
          aria-label={t('reservation.board.prefillAfterBookingCueAria', {
            defaultValue: '続けて予約：{{time}} {{name}} の直後の枠をフォームに入れる',
            time: timeLabel,
            name: nameLabel,
          })}
          onClick={() => onPrefillAfterBooking(booking)}
        >
          {t('reservation.board.prefillAfterBookingCue', { defaultValue: '続けて予約' })}
        </button>
      ) : null}
      <div className={styles.bookingItemTrailing}>
        <button
          type="button"
          className={`btn btn-sm ${styles.iconActionBtn} ${styles.bookingDeleteBtn}`}
          disabled={deletePending}
          title={deleteLabel}
          aria-label={deleteLabel}
          onClick={() => onDeleteBooking(booking)}
        >
          <i
            className={`fas ${booking.hasDeletePassword ? 'fa-lock' : 'fa-trash-alt'}`}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}
